import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import { useToast } from './ToastContext';
import Card from './components/Card';
import Badge from './components/Badge';
import Button from './components/Button';
import Loader from './components/Loader';
import ReferencePageBadge from './components/ReferencePageBadge';
import useReferencePages from './hooks/useReferencePages';
import useReferencePagesAudit from './hooks/useReferencePagesAudit';
import {
  getTechnicalErrors,
  getAudits,
  getAuditByUrl,
  getReferencePageTodos,
  generateReferencePageTodo,
} from './api';
import {
  getReferencePagesList,
  getSeoDetailPath,
  isReferencePage,
  mergeReferencePageUrls,
  normalizePageUrl,
  resolveReferenceUrlsForAudit,
} from './utils/referencePages';
import { buildPageVerdict, mergeSeoWithPageContent } from './utils/referencePageExport';
import { downloadReferencePagesCsv } from './utils/referencePagesCsvExport';
import {
  evaluateSeoCriteria,
  getTitleLengthStatus,
  getMetaLengthStatus,
} from './utils/seoCriteria';
import { isOperationalTechnicalError } from './utils/technicalErrors';
import { buildPageFindings } from './utils/auditFindings';
import { getScoreQualitative } from './utils/scoreLabels';
import { buildPagespeedSummaryFallback } from './utils/pagespeedSummary';
import LighthouseFindingList from './components/LighthouseFindingList';
import IATodoList from './components/IATodoList';
import SubNav from './components/SubNav';
import GoogleSerpPreview from './components/GoogleSerpPreview';
import HeadingsArchitecturePanel from './components/HeadingsArchitecturePanel';
import ImagesAltPanel from './components/ImagesAltPanel';
import CardFooterToggle from './components/CardFooterToggle';
import decodeHtml from './utils/decodeHtml';
import {
  resolveHeadingsData,
  getHeadingsStructureStatus,
} from './utils/headingsAnalysis';
import { resolveImagesList } from './utils/imagesAudit';
import {
  FileText, ExternalLink, Eye,
  Search, AlertTriangle, CheckCircle, XCircle, Gauge,
  ChevronDown, ChevronUp, RefreshCw, X, ClipboardList, ListChecks, Download,
} from 'lucide-react';

// ── Score pill coloré ─────────────────────────────────────────────────────────

function ScorePill({ value, label }) {
  const n = Math.round(Number(value) || 0);
  const has = n > 0;
  const q = getScoreQualitative(n);
  const color = !has
    ? 'bg-blue-gray-100 text-blue-gray-400'
    : `${q.bg} ${q.color}`.replace('text-blue-gray-400', 'text-blue-gray-500');
  const qual = !has ? '—' : q.label;
  return (
    <div className={`flex flex-col items-center rounded-xl px-3 py-2.5 border ${has ? 'border-transparent' : 'border-neutral-100'} ${color.split(' ')[0]} min-w-[76px]`}>
      <span className={`text-2xl font-bold leading-none ${color.split(' ').slice(1).join(' ')}`}>
        {has ? n : '—'}
      </span>
      <span className={`text-[10px] font-medium mt-0.5 ${color.split(' ').slice(1).join(' ')} opacity-80`}>
        {qual}
      </span>
      <span className="text-[10px] text-blue-gray-400 mt-0.5">{label}</span>
    </div>
  );
}

// ── Critère SEO en ligne ──────────────────────────────────────────────────────

const SEO_STATUS_BADGE = {
  ok: 'text-green-700 bg-green-50 border-green-200',
  warning: 'text-amber-800 bg-amber-50 border-amber-200',
  error: 'text-red-700 bg-red-50 border-red-200',
};

const SEO_TONE_TEXT = {
  ok: 'text-green-700',
  warning: 'text-amber-700',
  error: 'text-red-600',
};

/** Résumé compact sous l’aperçu Google — aligné sur le libellé du footer (sans puce décalante). */
function SummaryCriterionChip({ label, metric, status, tone }) {
  const t = tone || 'warning';
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] leading-snug">
      <span className="font-medium text-blue-gray-800">{label}</span>
      {metric != null && metric !== '' && (
        <span className="text-blue-gray-500 tabular-nums">{metric}</span>
      )}
      {status && (
        <span className={`font-medium ${SEO_TONE_TEXT[t]}`}>{status}</span>
      )}
    </span>
  );
}

function shortCriterionStatus(label) {
  const shortcuts = {
    'Longueur optimale': 'OK',
    Présente: 'OK',
    Absente: 'Manquant',
    'Trop longue': 'Trop long',
    'Trop courte': 'Trop court',
    'Trop long': 'Trop long',
    'Trop court': 'Trop court',
  };
  return shortcuts[label] || label;
}

/** Détail des critères — lisible, aéré */
const CRIT_ROW = 'px-3 py-2.5';
const CRIT_LABEL = 'text-sm font-medium text-blue-gray-800';
const CRIT_DETAIL = 'text-xs text-blue-gray-600 leading-relaxed';
const CRIT_STATUS_PILL = 'text-[10px] font-medium leading-none px-1.5 py-0.5 rounded-md border tabular-nums';

function SeoOnPageCriteriaPanel({
  className = '',
  titleLen,
  titleOk,
  titleStatus,
  meta,
  metaLen,
  metaOk,
  metaStatus,
  h1Ok,
  h1Count,
  h1Text,
  imagesOk,
  nbWithoutAlt,
  nbImages,
  canonOk,
  headingsList = [],
  headingsCounts = {},
  imagesList = [],
}) {
  const [open, setOpen] = useState(false);

  const h1StatusLabel = h1Count > 1 ? `${h1Count} H1` : h1Ok ? 'OK' : 'Absent';
  const h1Tone = h1Count > 1 ? 'warning' : h1Ok ? 'ok' : 'error';

  const imageTone = !nbImages ? 'ok' : imagesOk ? 'ok' : 'error';

  const headingsStatus = getHeadingsStructureStatus(headingsList, headingsCounts);

  const issueCount = [
    titleOk,
    metaOk,
    h1Ok,
    imagesOk,
    canonOk,
    headingsStatus.hasData ? headingsStatus.ok : null,
  ].filter((ok) => ok === false).length;

  const issueBadge = issueCount > 0 ? (
    <span className={`${CRIT_STATUS_PILL} text-amber-800 bg-amber-50 border-amber-200`}>
      {issueCount} à corriger
    </span>
  ) : null;

  return (
    <>
      <div className={className}>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2.5">
        <SummaryCriterionChip
          label="Title"
          metric={`${titleLen} car.`}
          status={shortCriterionStatus(titleStatus.label)}
          tone={titleStatus.tone}
        />
        <SummaryCriterionChip
          label="Meta"
          metric={meta ? `${metaLen} car.` : undefined}
          status={shortCriterionStatus(meta ? metaStatus.label : 'Absente')}
          tone={meta ? metaStatus.tone : 'error'}
        />
        <SummaryCriterionChip
          label="H1"
          status={h1StatusLabel}
          tone={h1Tone}
        />
        <SummaryCriterionChip
          label="Images"
          metric={nbImages > 0 ? `${nbImages - nbWithoutAlt}/${nbImages}` : undefined}
          status={nbImages > 0 ? undefined : 'Aucune'}
          tone={imageTone}
        />
        <SummaryCriterionChip
          label="Canonique"
          status={shortCriterionStatus(canonOk ? 'Présente' : 'Absente')}
          tone={canonOk ? 'ok' : 'error'}
        />
        <SummaryCriterionChip
          label="Titres"
          status={shortCriterionStatus(headingsStatus.label)}
          tone={headingsStatus.tone}
        />
        </div>
      </div>

      <CardFooterToggle
        label="Détail des critères on-page"
        badge={issueBadge}
        expanded={open}
        inset
        className="mt-3"
        onClick={() => setOpen((v) => !v)}
      />

      {open && (
        <div className="border-t border-neutral-200/80 bg-white overflow-hidden divide-y divide-neutral-100">
          <SeoRow
            label="Balise Title"
            ok={titleOk}
            value={titleLen ? `${titleLen} car.` : undefined}
            statusLabel={titleStatus.label}
            statusTone={titleStatus.tone}
            warning={titleStatus.tone === 'warning'}
            detail={!titleOk ? titleStatus.recommendation : undefined}
          />
          <SeoRow
            label="Meta description"
            ok={metaOk}
            warning={metaStatus.tone === 'warning'}
            value={meta ? `${metaLen} car.` : undefined}
            statusLabel={metaStatus.label}
            statusTone={metaStatus.tone}
            detail={
              !meta
                ? metaStatus.recommendation
                : !metaOk && metaStatus.recommendation
                  ? `${meta} — ${metaStatus.recommendation}`
                  : meta
            }
          />
          <SeoRow
            label="Balise H1"
            ok={h1Ok}
            value={h1Count > 1 ? `${h1Count} H1` : undefined}
            detail={
              h1Count > 1
                ? 'Plusieurs H1 détectés — un seul recommandé'
                : h1Text
                  ? `« ${h1Text.slice(0, 90)}${h1Text.length > 90 ? '…' : ''} »`
                  : 'Absent'
            }
          />
          <ImagesAltPanel
            images={imagesList}
            nbImages={nbImages}
            nbWithoutAlt={nbWithoutAlt}
            imagesOk={imagesOk}
          />
          <SeoRow
            label="Balise canonique"
            ok={canonOk}
            detail={canonOk ? 'Présente' : 'Absente'}
          />
          <HeadingsArchitecturePanel
            headingsList={headingsList}
            headingsCounts={headingsCounts}
          />
        </div>
      )}
    </>
  );
}

function SeoRow({ label, ok, value, statusLabel, statusTone, detail, warning = false }) {
  const iconColor = ok ? 'text-green-500' : warning ? 'text-amber-500' : 'text-red-400';
  const IconComp = ok ? CheckCircle : warning ? AlertTriangle : XCircle;
  const showDetail = !!detail;
  const badgeClass = SEO_STATUS_BADGE[statusTone] || SEO_STATUS_BADGE.warning;
  return (
    <div className={`flex items-start gap-2.5 ${CRIT_ROW}`}>
      <IconComp size={14} className={`${iconColor} shrink-0 mt-0.5`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-3">
          <span className={CRIT_LABEL}>{label}</span>
          {(value !== undefined || statusLabel) && (
            <div className="flex items-center gap-1.5 shrink-0">
              {value !== undefined && (
                <span className={`text-xs font-semibold tabular-nums ${ok ? 'text-green-600' : warning ? 'text-amber-600' : 'text-red-500'}`}>
                  {value}
                </span>
              )}
              {statusLabel && (
                <span className={`${CRIT_STATUS_PILL} ${badgeClass}`}>
                  {statusLabel}
                </span>
              )}
            </div>
          )}
        </div>
        {showDetail && <p className={`${CRIT_DETAIL} mt-1.5`}>{detail}</p>}
      </div>
    </div>
  );
}

// ── Barre de performance ──────────────────────────────────────────────────────

function PerfBar({ label, score }) {
  const n = Math.round(Number(score) || 0);
  if (!n) return null;
  const color = n >= 90 ? 'bg-green-500' : n >= 50 ? 'bg-amber-400' : 'bg-red-400';
  const textColor = n >= 90 ? 'text-green-600' : n >= 50 ? 'text-amber-600' : 'text-red-500';
  return (
    <div className="flex items-center gap-2.5">
      <span className="text-xs text-blue-gray-500 w-28 shrink-0">{label}</span>
      <div className="flex-1 h-1.5 bg-blue-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${n}%` }} />
      </div>
      <span className={`text-xs font-bold w-6 text-right ${textColor}`}>{n}</span>
    </div>
  );
}

// ── Verdict synthétique d'une page ────────────────────────────────────────────

const VERDICT_TONE = {
  red: 'text-red-600',
  amber: 'text-amber-600',
  green: 'text-green-600',
};

const VERDICT_ICONS = {
  red: XCircle,
  amber: AlertTriangle,
  green: CheckCircle,
};

function ReferencePageReportCard({
  page,
  seo,
  perf,
  errors,
  slug,
  todo,
  todoGenerating = false,
  onGenerateTodo,
  onOpenDetail,
  onReaudit,
  isReauditing = false,
  auditDisabled = false,
  onLoadPagespeedRaw,
  pagespeedDetailLoading = false,
  defaultExpanded = false,
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [pagespeedDetailOpen, setPagespeedDetailOpen] = useState(false);
  const mergedSeo = useMemo(() => mergeSeoWithPageContent(seo, page), [seo, page]);
  const criteria = evaluateSeoCriteria(mergedSeo);
  const { titleLen, metaLen, h1Ok, nbWithoutAlt, imagesOk, titleOk, metaOk, canonOk, h1Count } = criteria;
  const hasSeo = Number(mergedSeo?.score_global) > 0;
  const hasPerf = perf?.pagespeed_average > 0;

  // Liste UNIQUE des constats de la page (SEO + perf + technique fusionnés et priorisés).
  const pageFindings = useMemo(
    () => buildPageFindings({ url: page.url, seo: mergedSeo, perf, errors }),
    [page.url, mergedSeo, perf, errors],
  );

  const highCount = pageFindings.filter((f) => f.severity === 'high').length;
  const verdict = buildPageVerdict({ highCount, total: pageFindings.length });
  const VerdictIcon = VERDICT_ICONS[verdict.tone] || AlertTriangle;

  const title = decodeHtml(mergedSeo?.title || '');
  const meta = decodeHtml(mergedSeo?.meta_description || '');
  const h1Text = decodeHtml(mergedSeo?.h1 || '');
  const titleStatus = getTitleLengthStatus(titleLen, !!mergedSeo?.title);
  const metaStatus = getMetaLengthStatus(metaLen, !!meta);
  const { headingsList, headingsCounts } = useMemo(
    () => resolveHeadingsData(mergedSeo, h1Text),
    [mergedSeo, h1Text],
  );
  const imagesList = useMemo(() => resolveImagesList(mergedSeo), [mergedSeo]);

  const pagespeedSummary = useMemo(() => {
    const fromPlan = (todo?.pagespeed_summary || '').trim();
    if (fromPlan) return fromPlan;
    return buildPagespeedSummaryFallback(perf);
  }, [todo?.pagespeed_summary, perf]);

  useEffect(() => {
    if (!expanded) setPagespeedDetailOpen(false);
  }, [expanded]);

  const ensurePagespeedRaw = useCallback(() => {
    if (hasPerf && !perf?.pagespeed_raw && onLoadPagespeedRaw) {
      onLoadPagespeedRaw(page.url);
    }
  }, [hasPerf, perf?.pagespeed_raw, onLoadPagespeedRaw, page.url]);

  useEffect(() => {
    if (defaultExpanded && hasPerf) ensurePagespeedRaw();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ouverture initiale uniquement
  }, []);

  // Au dépliage de la fiche, charge le détail PageSpeed pour la liste d'audits.
  const handleToggle = () => {
    const willOpen = !expanded;
    setExpanded(willOpen);
    if (willOpen) ensurePagespeedRaw();
  };

  const handlePagespeedDetailToggle = () => {
    const willOpen = !pagespeedDetailOpen;
    setPagespeedDetailOpen(willOpen);
    if (willOpen) ensurePagespeedRaw();
  };

  return (
    <article className="rounded-2xl border-2 border-accent/20 bg-white shadow-sm overflow-hidden print:break-inside-avoid">

      {/* ── En-tête : tout l'essentiel lisible sans déplier ──────────────────── */}
      <header
        className="px-5 py-4 bg-accent/[0.05] border-b border-accent/10 flex flex-wrap items-start justify-between gap-3 cursor-pointer select-none"
        onClick={handleToggle}
      >
        <div className="min-w-0 flex-1">
          <h3 className="font-heading text-base font-semibold text-blue-gray-900 leading-snug">
            {title || <span className="text-blue-gray-400 italic">Sans titre</span>}
          </h3>
          <a
            href={page.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-accent hover:underline break-all flex items-center gap-1 mt-1"
            onClick={(e) => e.stopPropagation()}
          >
            {page.url.replace(/^https?:\/\//, '')}
            <ExternalLink size={11} />
          </a>
          {/* Verdict d'une ligne — la lecture « clé en main » */}
          <p className={`flex items-center gap-1.5 text-sm font-medium mt-2 ${VERDICT_TONE[verdict.tone]}`}>
            <VerdictIcon size={14} className="shrink-0" />
            {verdict.text}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="hidden sm:flex gap-2">
            {hasSeo && <ScorePill value={mergedSeo.score_global} label="SEO" />}
            {hasPerf && <ScorePill value={perf.pagespeed_average} label="Perf." />}
          </div>
          {onReaudit && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onReaudit(page.url);
              }}
              disabled={auditDisabled}
              className="print:hidden p-1.5 rounded-lg border border-blue-gray-200 text-blue-gray-500 hover:text-accent hover:border-accent/40 transition disabled:opacity-50 disabled:cursor-not-allowed"
              title="Relancer l'audit SEO et PageSpeed pour cette page"
            >
              <RefreshCw size={15} className={isReauditing ? 'animate-spin text-accent' : ''} />
            </button>
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onOpenDetail(page.url); }}
            className="print:hidden p-1.5 rounded-lg border border-blue-gray-200 text-blue-gray-500 hover:text-accent hover:border-accent/40 transition"
            title="Fiche détaillée"
          >
            <Eye size={15} />
          </button>
          {expanded ? <ChevronUp size={16} className="text-blue-gray-400" /> : <ChevronDown size={16} className="text-blue-gray-400" />}
        </div>
      </header>

      {expanded && (
        <div className="p-5 flex flex-col gap-5">

          {/* Scores (mobile) */}
          <div className="sm:hidden flex gap-3 flex-wrap">
            {hasSeo && <ScorePill value={mergedSeo.score_global} label="SEO" />}
            {hasPerf && <ScorePill value={perf.pagespeed_average} label="Perf." />}
          </div>

          {/* 1. Contenu SEO actuel */}
          <section className="rounded-xl border border-neutral-200 bg-neutral-50/60 overflow-hidden">
            <h4 className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-blue-gray-500 flex items-center gap-2 border-b border-neutral-100">
              <Search size={12} /> Contenu SEO actuel
            </h4>
            <div className="px-4 pt-3 pb-1">
              <GoogleSerpPreview
                title={title}
                url={page.url}
                metaDescription={meta}
                titleStatus={titleStatus}
                titleLen={mergedSeo?.title ? titleLen : undefined}
                scale={0.82}
                showTitleBadge={false}
              />
              <SeoOnPageCriteriaPanel
                className="pt-4 mt-4 border-t border-neutral-200/80"
                titleLen={titleLen}
                titleOk={titleOk}
                titleStatus={titleStatus}
                meta={meta}
                metaLen={metaLen}
                metaOk={metaOk}
                metaStatus={metaStatus}
                h1Ok={h1Ok}
                h1Count={h1Count}
                h1Text={h1Text}
                imagesOk={imagesOk}
                nbWithoutAlt={nbWithoutAlt}
                nbImages={mergedSeo?.nb_images ?? 0}
                canonOk={canonOk}
                headingsList={headingsList}
                headingsCounts={headingsCounts}
                imagesList={imagesList}
              />
            </div>
          </section>

          {/* 2. Google PageSpeed — synthèse, compteurs, détail en accordéon */}
          {hasPerf && (
            <section className="rounded-xl border border-neutral-200 bg-neutral-50/60 overflow-hidden">
              <h4 className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-blue-gray-500 flex items-center gap-2 border-b border-neutral-100 bg-white/80">
                <Gauge size={12} /> Google PageSpeed
              </h4>
              <div className="px-4 py-4 bg-white/80 border-b border-neutral-100">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-blue-gray-400 mb-2">
                  Synthèse du rapport
                </p>
                {todoGenerating ? (
                  <Loader label="Élaboration de la synthèse PageSpeed…" />
                ) : pagespeedSummary ? (
                  <p className="text-base text-blue-gray-800 leading-relaxed">
                    {pagespeedSummary}
                  </p>
                ) : pagespeedDetailLoading ? (
                  <Loader label="Chargement des données PageSpeed…" />
                ) : (
                  <p className="text-sm text-blue-gray-500">
                    Synthèse indisponible. Relancez l&apos;audit de la page pour obtenir le rapport complet.
                  </p>
                )}
                {perf?.pagespeed_error && (
                  <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3">
                    Mesure PageSpeed : {perf.pagespeed_error}
                  </p>
                )}
              </div>
              <div className="px-4 py-3 bg-white/60">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                  <PerfBar label="Desktop" score={perf.pagespeed_performance_desktop} />
                  <PerfBar label="Mobile" score={perf.pagespeed_performance_mobile} />
                  <PerfBar label="Accessibilité" score={perf.pagespeed_accessibility} />
                  <PerfBar label="Bonnes pratiques" score={perf.pagespeed_best_practices} />
                  {perf.pagespeed_seo > 0 && (
                    <PerfBar label="SEO (Lighthouse)" score={perf.pagespeed_seo} />
                  )}
                </div>
              </div>
              <CardFooterToggle
                label="Métriques ressenties et audits PageSpeed"
                expanded={pagespeedDetailOpen}
                stopPropagation
                onClick={() => handlePagespeedDetailToggle()}
              />
              {pagespeedDetailOpen && (
                <div className="px-4 pb-4 pt-3 border-t border-neutral-200/80 bg-white/70">
                  {pagespeedDetailLoading && !perf?.pagespeed_raw ? (
                    <Loader label="Chargement du détail PageSpeed…" />
                  ) : perf?.pagespeed_raw ? (
                    <LighthouseFindingList
                      pagespeedRaw={perf.pagespeed_raw}
                      compact
                      groupByAxe={false}
                      showVitals
                      initialLimit={8}
                      embedded
                    />
                  ) : (
                    <p className="text-sm text-blue-gray-500 py-2">
                      Détail PageSpeed indisponible pour cette URL.
                    </p>
                  )}
                </div>
              )}
            </section>
          )}

          {/* 3. Plan d'action */}
          <section className="rounded-xl border border-accent/20 bg-white overflow-hidden">
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-accent/10">
              <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-blue-gray-500">
                <ClipboardList size={12} /> Plan d&apos;action
                {todo?.stale && !todoGenerating && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full normal-case tracking-normal">
                    <AlertTriangle size={9} /> Données d&apos;audit plus récentes
                  </span>
                )}
              </h4>
              {onGenerateTodo && !todoGenerating && (
                <button
                  type="button"
                  onClick={() => onGenerateTodo(page.resolvedUrl || page.url)}
                  className="print:hidden text-xs font-medium text-accent hover:underline inline-flex items-center gap-1"
                  title="Mettre à jour le plan d'action"
                >
                  <RefreshCw size={12} />
                  {todo?.todos?.length ? 'Actualiser' : 'Établir le plan'}
                </button>
              )}
            </div>
            <div className="px-4 py-4">
              {todoGenerating ? (
                <Loader label="Élaboration du plan d'action…" />
              ) : todo?.todos?.length ? (
                <IATodoList
                  plan={todo}
                  storageKey={`refpage-todo-${slug}-${normalizePageUrl(page.resolvedUrl || page.url)}`}
                  showSummary={false}
                />
              ) : (
                <p className="text-sm text-blue-gray-600">
                  {hasPerf || hasSeo
                    ? "Le plan d'action sera établi après le prochain audit de cette page, ou via « Établir le plan »."
                    : "Auditez d'abord cette page (bouton ↻) pour obtenir le plan d'action."}
                </p>
              )}
            </div>
          </section>

        </div>
      )}
    </article>
  );
}

export default function ProjectReferencePages() {
  const { project, refreshProject } = useOutletContext();
  const navigate = useNavigate();
  const { slug } = useParams();
  const { showToast } = useToast();
  const [mainTab, setMainTab] = useState('report');
  const [technicalByUrl, setTechnicalByUrl] = useState({});
  const [techLoading, setTechLoading] = useState(false);

  const savedPages = useMemo(
    () => getReferencePagesList(project),
    [project?.reference_pages],
  );

  const {
    sitemapUrls,
    selectedReferencePages,
    loading: refLoading,
    saved: referencePagesSaved,
    data: referencePagesData,
    activeTab: selectionTab,
    setActiveTab: setSelectionTab,
    togglePage: handleReferencePageToggle,
    addCustomUrl: handleAddCustomUrl,
    saveReferencePages: handleSaveReferencePages,
    getSortedUrls,
    fetchData,
  } = useReferencePages(project, null);

  const displayPages = useMemo(
    () => mergeReferencePageUrls(selectedReferencePages, savedPages),
    [selectedReferencePages, savedPages],
  );

  const displayPagesKey = useMemo(
    () => displayPages.map((u) => normalizePageUrl(u)).filter(Boolean).sort().join('|'),
    [displayPages],
  );
  const lastFetchedPagesKeyRef = React.useRef('');

  const hasUnsavedPages = useMemo(() => {
    const savedSet = new Set(savedPages.map(normalizePageUrl));
    return displayPages.some((u) => !savedSet.has(normalizePageUrl(u)));
  }, [displayPages, savedPages]);

  const [customUrlInput, setCustomUrlInput] = useState('');

  const {
    isAuditing: refAuditLoading,
    progress: refAuditProgress,
    currentUrl: refAuditCurrentUrl,
    auditPages: runReferencePagesAudit,
    cancel: cancelReferenceAudit,
  } = useReferencePagesAudit(slug);

  const reloadAudits = useCallback(async () => {
    if (!slug) return;
    const rows = await getAudits(slug).catch(() => []);
    setAuditRows(Array.isArray(rows) ? rows : []);
  }, [slug]);

  const reloadTechnicalErrors = useCallback(async () => {
    if (!slug || displayPages.length === 0) return;
    setTechLoading(true);
    try {
      const data = await getTechnicalErrors(slug);
      const map = {};
      for (const page of data?.errors || []) {
        if (page?.url) {
          const key = normalizePageUrl(page.url);
          map[key] = page.errors || [];
          map[page.url] = page.errors || [];
        }
      }
      setTechnicalByUrl(map);
    } catch {
      setTechnicalByUrl({});
    } finally {
      setTechLoading(false);
    }
  }, [slug, displayPages.length]);

  const [pagespeedRawByNorm, setPagespeedRawByNorm] = useState({});
  const [pagespeedRawLoadingNorms, setPagespeedRawLoadingNorms] = useState(() => new Set());
  const pagespeedRawInFlightRef = React.useRef(new Set());
  const pagespeedRawCacheRef = React.useRef({});
  const pagespeedAttemptedRef = React.useRef(new Set());

  // Plans d'action par page (cache base, généré au ré-audit). Clé = normalizePageUrl.
  const [todosByNorm, setTodosByNorm] = useState({});
  const [todoGeneratingNorms, setTodoGeneratingNorms] = useState(() => new Set());
  const [todoBulkProgress, setTodoBulkProgress] = useState(null); // { done, total } | null
  const todosLoadedRef = React.useRef(false);

  const reloadTodos = useCallback(async () => {
    if (!slug) return;
    try {
      const map = await getReferencePageTodos(slug);
      const next = {};
      for (const [key, value] of Object.entries(map || {})) {
        const norm = normalizePageUrl(key);
        if (norm) next[norm] = value;
      }
      setTodosByNorm(next);
    } catch {
      // cache indisponible : on n'écrase pas l'existant
    }
  }, [slug]);

  const generateTodosForUrls = useCallback(
    async (urls) => {
      if (!slug) return;
      const list = (urls || []).filter(
        (u) => typeof u === 'string' && /^https?:\/\//i.test(u.trim()),
      );
      if (!list.length) return;
      setTodoBulkProgress(list.length > 1 ? { done: 0, total: list.length } : null);
      for (let i = 0; i < list.length; i++) {
        const url = list[i].trim();
        const norm = normalizePageUrl(url);
        setTodoGeneratingNorms((prev) => new Set(prev).add(norm));
        let keyMissing = false;
        try {
          const result = await generateReferencePageTodo(slug, url);
          if (result) {
            setTodosByNorm((prev) => ({ ...prev, [norm]: result }));
          }
        } catch (err) {
          // Ne pas bloquer l'audit si l'établissement du plan échoue sur une page.
          if (err?.response?.status === 403) keyMissing = true;
        } finally {
          setTodoGeneratingNorms((prev) => {
            const nextSet = new Set(prev);
            nextSet.delete(norm);
            return nextSet;
          });
          if (list.length > 1) {
            setTodoBulkProgress({ done: i + 1, total: list.length });
          }
        }
        if (keyMissing) {
          showToast(
            'Clé API non configurée : les plans d\'action n\'ont pas pu être établis.',
            'error',
          );
          break;
        }
      }
      setTodoBulkProgress(null);
    },
    [slug, showToast],
  );

  const storePagespeedRaw = useCallback((raw, ...urls) => {
    if (!raw) return;
    const keys = new Set();
    for (const u of urls) {
      const n = normalizePageUrl(u);
      if (n) keys.add(n);
    }
    if (keys.size === 0) return;
    setPagespeedRawByNorm((prev) => {
      const next = { ...prev };
      for (const k of keys) next[k] = raw;
      pagespeedRawCacheRef.current = next;
      return next;
    });
  }, []);

  const mergeAuditResult = useCallback((url, result) => {
    if (!result || result.error) return;
    const norm = normalizePageUrl(url);
    if (result.pagespeed_raw) {
      storePagespeedRaw(result.pagespeed_raw, url, result.url);
    }
    setAuditRows((prev) => {
      const idx = prev.findIndex(
        (r) => r.url === url || normalizePageUrl(r.url) === norm,
      );
      const row = { ...result, url: result.url || url };
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], ...row };
        return next;
      }
      return [...prev, row];
    });
  }, [storePagespeedRaw]);

  const requestPagespeedRaw = useCallback(
    async (url) => {
      if (!slug || !url) return;
      const norm = normalizePageUrl(url);
      if (!norm) return;
      if (
        pagespeedRawCacheRef.current[norm]
        || pagespeedRawInFlightRef.current.has(norm)
        || pagespeedAttemptedRef.current.has(norm)
      ) {
        return;
      }

      pagespeedRawInFlightRef.current.add(norm);
      setPagespeedRawLoadingNorms((prev) => new Set(prev).add(norm));

      const resolved = resolveReferenceUrlsForAudit([url], project?.url)[0];
      try {
        pagespeedAttemptedRef.current.add(norm);
        if (resolved) {
          const audit = await getAuditByUrl(slug, resolved);
          if (audit?.pagespeed_raw) {
            storePagespeedRaw(audit.pagespeed_raw, url, audit.url, resolved);
          }
        }
      } catch {
        // Pas d'audit ou URL non trouvée
      } finally {
        pagespeedRawInFlightRef.current.delete(norm);
        setPagespeedRawLoadingNorms((prev) => {
          const next = new Set(prev);
          next.delete(norm);
          return next;
        });
      }
    },
    [slug, project?.url, storePagespeedRaw],
  );

  const afterReferenceAudit = useCallback(async () => {
    await reloadAudits();
    await reloadTechnicalErrors();
    if (displayPages.length > 0) {
      lastFetchedPagesKeyRef.current = '';
      fetchData(displayPages);
    }
    if (refreshProject) await refreshProject();
  }, [
    reloadAudits,
    reloadTechnicalErrors,
    displayPages,
    fetchData,
    refreshProject,
  ]);

  const handleAuditReferencePages = useCallback(async () => {
    const urls = resolveReferenceUrlsForAudit(displayPages, project?.url);
    if (!urls.length) {
      return;
    }
    const res = await runReferencePagesAudit(urls, {
      onPageDone: mergeAuditResult,
      onComplete: afterReferenceAudit,
    });
    if (!res?.cancelled) {
      // Établit le plan d'action des pages auditées (séquentiel, non bloquant).
      await generateTodosForUrls(urls);
    }
  }, [
    displayPages,
    project?.url,
    runReferencePagesAudit,
    mergeAuditResult,
    afterReferenceAudit,
    generateTodosForUrls,
  ]);

  const handleAuditSingleReferencePage = useCallback(
    async (url) => {
      const urls = resolveReferenceUrlsForAudit([url], project?.url);
      if (!urls.length) return;
      const res = await runReferencePagesAudit(urls, {
        onPageDone: mergeAuditResult,
        onComplete: afterReferenceAudit,
      });
      if (!res?.cancelled) {
        await generateTodosForUrls(urls);
      }
    },
    [project?.url, runReferencePagesAudit, mergeAuditResult, afterReferenceAudit, generateTodosForUrls],
  );

  // Génération manuelle depuis une fiche, à partir d'un audit EXISTANT.
  // Si la page n'a jamais été auditée (404), on ne déclenche PAS d'audit ici
  // (un audit lancé en parallèle pour plusieurs pages provoque des écrasements
  // de lignes) : on invite à utiliser le bouton « ré-auditer » (↻) de la fiche,
  // qui audite la page séquentiellement puis génère le plan automatiquement.
  const handleGenerateSingleTodo = useCallback(
    async (url) => {
      if (!slug) return;
      const resolved = resolveReferenceUrlsForAudit([url], project?.url)[0] || url;
      const norm = normalizePageUrl(resolved);
      setTodoGeneratingNorms((prev) => new Set(prev).add(norm));
      try {
        const result = await generateReferencePageTodo(slug, resolved);
        if (result) setTodosByNorm((prev) => ({ ...prev, [norm]: result }));
      } catch (err) {
        const code = err?.response?.status;
        if (code === 404) {
          showToast(
            "Cette page n'a pas encore d'audit : lancez d'abord son audit avec le bouton ↻ de la fiche.",
            'info',
          );
        } else if (code === 403) {
          showToast('Clé API non configurée : ajoutez-la dans les paramètres.', 'error');
        } else {
          showToast("Échec de l'établissement du plan d'action.", 'error');
        }
      } finally {
        setTodoGeneratingNorms((prev) => {
          const next = new Set(prev);
          next.delete(norm);
          return next;
        });
      }
    },
    [slug, project?.url, showToast],
  );

  const isPageBeingAudited = useCallback(
    (url) => {
      if (!refAuditLoading || !refAuditCurrentUrl || !url) return false;
      return normalizePageUrl(url) === normalizePageUrl(refAuditCurrentUrl);
    },
    [refAuditLoading, refAuditCurrentUrl],
  );

  const auditTargetCount = displayPages.length;

  useEffect(() => {
    if (mainTab !== 'report' || !displayPagesKey) return;
    if (lastFetchedPagesKeyRef.current === displayPagesKey) return;
    lastFetchedPagesKeyRef.current = displayPagesKey;
    fetchData(displayPages);
  }, [mainTab, displayPagesKey, displayPages, fetchData]);

  // Précharge le détail PageSpeed de toutes les pages de référence : compteurs
  // stables (plus de saut au dépliage) et retours Google disponibles d'emblée.
  useEffect(() => {
    if (mainTab !== 'report' || displayPages.length === 0) return;
    displayPages.forEach((url) => requestPagespeedRaw(url));
  }, [mainTab, displayPagesKey, displayPages, requestPagespeedRaw]);

  // Charge les plans d'action en cache à l'ouverture du rapport.
  useEffect(() => {
    if (mainTab !== 'report' || todosLoadedRef.current) return;
    todosLoadedRef.current = true;
    reloadTodos();
  }, [mainTab, reloadTodos]);

  // Un seul appel getAudits : il contient à la fois les données SEO et PageSpeed
  // (évite de relancer les hooks complets SEO + Performance, soit 4 requêtes).
  const [auditRows, setAuditRows] = useState([]);
  useEffect(() => {
    if (!slug) return;
    getAudits(slug)
      .then((rows) => setAuditRows(Array.isArray(rows) ? rows : []))
      .catch(() => setAuditRows([]));
  }, [slug, project?.date_dernier_audit]);

  useEffect(() => {
    if (!slug || displayPages.length === 0) return;
    setTechLoading(true);
    getTechnicalErrors(slug)
      .then((data) => {
        const map = {};
        for (const page of data?.errors || []) {
          if (page?.url) {
            const key = normalizePageUrl(page.url);
            map[key] = page.errors || [];
            map[page.url] = page.errors || [];
          }
        }
        setTechnicalByUrl(map);
      })
      .catch(() => setTechnicalByUrl({}))
      .finally(() => setTechLoading(false));
  }, [slug, displayPages.length, project?.updated_at]);

  // seoByUrl et perfByUrl proviennent de la même source (auditRows).
  const seoByUrl = useMemo(() => {
    const m = {};
    for (const row of auditRows || []) {
      if (row?.url) {
        m[row.url] = row;
        m[normalizePageUrl(row.url)] = row;
      }
    }
    return m;
  }, [auditRows]);

  const perfByUrl = seoByUrl;

  const reportPages = useMemo(() => {
    return displayPages.map((url) => {
      const norm = normalizePageUrl(url);
      const content = referencePagesData?.find(
        (d) => d.url === url || normalizePageUrl(d.url) === norm,
      );
      const seo = seoByUrl[url] || seoByUrl[norm];
      const perfBase = perfByUrl[url] || perfByUrl[norm];
      const raw = pagespeedRawByNorm[norm] || pagespeedRawByNorm[url];
      const perf = perfBase
        ? { ...perfBase, pagespeed_raw: perfBase.pagespeed_raw || raw }
        : null;
      const errors = (technicalByUrl[url] || technicalByUrl[norm] || []).filter(
        (e) => !isOperationalTechnicalError(e),
      );
      const resolvedUrl = resolveReferenceUrlsForAudit([url], project?.url)[0] || url;
      return {
        url,
        resolvedUrl,
        title: content?.title || seo?.title,
        meta_description: content?.meta_description || seo?.meta_description,
        seo,
        perf,
        errors,
      };
    });
  }, [displayPages, referencePagesData, seoByUrl, perfByUrl, technicalByUrl, pagespeedRawByNorm, project?.url]);

  const isPagespeedDetailLoading = useCallback(
    (url) => pagespeedRawLoadingNorms.has(normalizePageUrl(url)),
    [pagespeedRawLoadingNorms],
  );

  const handleOpenDetail = useCallback(
    (url) => {
      const path = getSeoDetailPath(slug, url);
      if (path) navigate(path);
    },
    [navigate, slug]
  );

  const [csvExporting, setCsvExporting] = useState(false);

  const handleExportReferenceCsv = useCallback(async () => {
    if (!reportPages.length) {
      showToast('Aucune page de référence à exporter.', 'warning');
      return;
    }
    setCsvExporting(true);
    try {
      showToast('Préparation de l’export (données PageSpeed)…', 'info');
      await downloadReferencePagesCsv(slug, project, reportPages, todosByNorm);
      showToast('Export CSV téléchargé — prêt pour synthèse IA.', 'success');
    } catch (e) {
      showToast(e?.message || 'Export CSV impossible.', 'error');
    } finally {
      setCsvExporting(false);
    }
  }, [slug, project, reportPages, todosByNorm, showToast]);

  return (
    <div className="mx-auto w-full px-2 md:px-0 flex flex-col gap-6 reference-pages-view">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-blue-gray-900 mb-1">Pages de référence</h1>
          <p className="text-sm text-blue-gray-600 max-w-2xl">
            Sélectionnez vos pages stratégiques. Elles sont mises en avant dans tout l&apos;audit et regroupées ici avec un rapport détaillé.
          </p>
        </div>
        {auditTargetCount > 0 && (
          <div className="flex gap-2 print:hidden flex-wrap justify-end items-center">
            <Button
              variant="secondary"
              className="!inline-flex shrink-0"
              onClick={handleAuditReferencePages}
              disabled={refAuditLoading || refLoading}
              title="Ré-analyse SEO et PageSpeed uniquement sur ces pages — le reste du projet n'est pas modifié"
            >
              <RefreshCw size={14} className={`mr-1.5 shrink-0 ${refAuditLoading ? 'animate-spin' : ''}`} />
              {refAuditLoading ? 'Audit…' : `Relancer l'audit (${auditTargetCount})`}
            </Button>
            {savedPages.length > 0 && mainTab === 'report' && (
              <Button
                variant="secondary"
                className="!inline-flex shrink-0"
                onClick={handleExportReferenceCsv}
                disabled={csvExporting || refLoading || !reportPages.length}
                title="Export CSV du contenu complet des fiches (SEO, PageSpeed, constats, plan IA) pour synthèse dans Claude ou autre IA"
              >
                <Download size={14} className={`mr-1.5 shrink-0 ${csvExporting ? 'animate-pulse' : ''}`} />
                {csvExporting ? 'Export…' : 'Exporter CSV (IA)'}
              </Button>
            )}
          </div>
        )}
      </div>

      {refAuditLoading && (
        <Card className="p-4 border-accent/20 bg-accent/5">
          <div className="flex items-center justify-between gap-3 mb-2">
            <p className="text-sm font-medium text-blue-gray-800">
              Audit des pages de référence en cours…
            </p>
            <button
              type="button"
              onClick={cancelReferenceAudit}
              className="text-xs text-blue-gray-500 hover:text-red-600 flex items-center gap-1"
            >
              <X size={12} /> Annuler
            </button>
          </div>
          <div className="h-2 bg-blue-gray-100 rounded-full overflow-hidden mb-2">
            <div
              className="h-full bg-accent transition-all duration-300"
              style={{ width: `${refAuditProgress}%` }}
            />
          </div>
          {refAuditCurrentUrl && (
            <p className="text-xs text-blue-gray-500 truncate font-mono">
              {refAuditCurrentUrl.replace(/^https?:\/\//, '')}
            </p>
          )}
          <p className="text-[11px] text-blue-gray-400 mt-1">
            Seules ces pages sont mises à jour. Le tableau URL &amp; SEO conserve le reste de vos audits.
          </p>
        </Card>
      )}

      {todoBulkProgress && (
        <Card className="p-4 border-accent/20 bg-accent/5">
          <p className="text-sm font-medium text-blue-gray-800 flex items-center gap-2 mb-2">
            <ClipboardList size={14} className="text-accent" />
            Élaboration des plans d&apos;action… ({todoBulkProgress.done}/{todoBulkProgress.total})
          </p>
          <div className="h-2 bg-blue-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-accent transition-all duration-300"
              style={{ width: `${Math.round((todoBulkProgress.done / todoBulkProgress.total) * 100)}%` }}
            />
          </div>
        </Card>
      )}

      <SubNav
        active={mainTab}
        onChange={setMainTab}
        items={[
          {
            key: 'report',
            label: `Rapport détaillé (${displayPages.length})`,
            icon: FileText,
          },
          {
            key: 'selection',
            label: `Sélection (${selectedReferencePages?.length || 0})`,
            icon: ListChecks,
          },
        ]}
      />

      {mainTab === 'selection' && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-semibold text-blue-gray-900">Choisir les pages</h2>
            {referencePagesSaved && <Badge color="success">Enregistrées</Badge>}
          </div>
          <p className="text-sm text-blue-gray-500 mb-4">
            Jusqu&apos;à 10 pages. Les URLs sélectionnées apparaissent en premier dans les tableaux SEO et Performance.
            Utilisez <strong className="font-medium text-blue-gray-600">Relancer l&apos;audit</strong> pour
            rafraîchir uniquement ces pages sans toucher aux autres URLs déjà auditées.
          </p>

          <div className="flex gap-2 mb-4 border-b border-neutral-100 pb-2">
            {[
              ['selection', `Liste (${selectedReferencePages?.length || 0})`],
              ['utilisees', 'Aperçu contenu'],
            ].map(([k, l]) => (
              <button
                key={k}
                type="button"
                onClick={() => setSelectionTab(k)}
                className={`text-sm pb-2 border-b-2 transition-colors ${
                  selectionTab === k
                    ? 'font-semibold text-accent border-accent'
                    : 'text-neutral-500 border-transparent'
                }`}
              >
                {l}
              </button>
            ))}
          </div>

          {selectionTab === 'selection' ? (
            <>
              <div className="mb-4 p-3 rounded-lg border border-blue-gray-200 bg-blue-gray-50/60">
                <p className="text-xs font-medium text-blue-gray-700 mb-2">
                  Ajouter une URL hors sitemap (landing, page stratégique non listée…)
                </p>
                <form
                  className="flex flex-col sm:flex-row gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (handleAddCustomUrl(customUrlInput)) setCustomUrlInput('');
                  }}
                >
                  <input
                    type="text"
                    value={customUrlInput}
                    onChange={(e) => setCustomUrlInput(e.target.value)}
                    placeholder="https://… ou /chemin-de-page"
                    className="flex-1 text-sm border border-blue-gray-200 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent/40 bg-white"
                  />
                  <Button type="submit" size="sm" variant="secondary" disabled={refLoading}>
                    Ajouter
                  </Button>
                </form>
              </div>
              <div className="border border-neutral-200 rounded-lg p-2 bg-neutral-50 max-h-80 overflow-y-auto">
                {getSortedUrls().map((url) => (
                  <label
                    key={url}
                    className={`flex items-center gap-3 p-2 rounded-md hover:bg-white cursor-pointer ${
                      isReferencePage(url, { reference_pages: selectedReferencePages })
                        ? 'bg-accent/[0.06]'
                        : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-accent border-gray-300 rounded focus:ring-accent"
                      checked={selectedReferencePages?.includes(url)}
                      onChange={() => handleReferencePageToggle(url)}
                    />
                    <span className="text-sm text-neutral-700 truncate flex-1">{url}</span>
                    {selectedReferencePages?.includes(url) && <ReferencePageBadge compact />}
                  </label>
                ))}
                {sitemapUrls.length === 0 && !selectedReferencePages?.length && (
                  <p className="p-4 text-center text-sm text-neutral-500">
                    Aucune URL dans le sitemap — ajoutez des pages manuellement ci-dessus.
                  </p>
                )}
              </div>
              <div className="mt-4 text-right">
                <Button
                  size="sm"
                  onClick={async () => {
                    await handleSaveReferencePages();
                    if (refreshProject) await refreshProject();
                    setMainTab('report');
                  }}
                  disabled={refLoading}
                >
                  {refLoading ? 'Enregistrement…' : 'Enregistrer et voir le rapport'}
                </Button>
              </div>
            </>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {selectedReferencePages?.map((url) => {
                const norm = normalizePageUrl(url);
                const p = referencePagesData?.find(
                  (d) => d.url === url || normalizePageUrl(d.url) === norm,
                );
                return (
                  <div key={url} className="p-3 bg-neutral-50 rounded-lg border border-neutral-100">
                    <div className="font-medium text-neutral-800">{p?.title || url}</div>
                    {p?.meta_description && (
                      <div className="text-neutral-500 text-sm mt-1">{p.meta_description}</div>
                    )}
                  </div>
                );
              })}
              {!selectedReferencePages?.length && (
                <p className="text-sm text-neutral-500 text-center py-6">Aucune page sélectionnée.</p>
              )}
            </div>
          )}
        </Card>
      )}

      {mainTab === 'report' && (
        <>
          {displayPages.length === 0 ? (
            <Card className="p-10 text-center">
              <h2 className="font-heading text-lg font-semibold text-blue-gray-900 mb-2">
                Aucune page de référence
              </h2>
              <p className="text-sm text-blue-gray-600 mb-6 max-w-md mx-auto">
                Passez par l&apos;onglet Sélection pour choisir vos pages prioritaires, puis consultez ici le rapport consolidé.
              </p>
              <Button onClick={() => setMainTab('selection')}>Sélectionner des pages</Button>
            </Card>
          ) : (
            <>
              {hasUnsavedPages && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 print:hidden">
                  Certaines pages du rapport ne sont pas encore enregistrées.
                  Cliquez sur <strong>Enregistrer et voir le rapport</strong> dans l&apos;onglet Sélection
                  pour les conserver et les mettre en avant dans tout l&apos;audit.
                </div>
              )}
              {techLoading && <Loader label="Chargement des erreurs techniques…" />}

              <div id="reference-pages-report" className="space-y-6">
                {reportPages.map((page, index) => {
                  const todoNorm = normalizePageUrl(page.resolvedUrl || page.url);
                  const altNorm = normalizePageUrl(page.url);
                  return (
                    <ReferencePageReportCard
                      key={page.url}
                      defaultExpanded={index === 0}
                      page={page}
                      seo={page.seo}
                      perf={page.perf}
                      errors={page.errors}
                      slug={slug}
                      todo={todosByNorm[todoNorm] || todosByNorm[altNorm]}
                      todoGenerating={
                        todoGeneratingNorms.has(todoNorm) || todoGeneratingNorms.has(altNorm)
                      }
                      onGenerateTodo={handleGenerateSingleTodo}
                      onOpenDetail={handleOpenDetail}
                      onReaudit={handleAuditSingleReferencePage}
                      isReauditing={isPageBeingAudited(page.url)}
                      auditDisabled={refAuditLoading}
                      onLoadPagespeedRaw={requestPagespeedRaw}
                      pagespeedDetailLoading={isPagespeedDetailLoading(page.url)}
                    />
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
